/**
 * Shared visual identifier for projects + workspaces. Priority:
 *   1. Uploaded image (via R2 — image_key + base URL)
 *   2. User-chosen emoji
 *   3. Colored circle with first letter of name (legacy fallback)
 *
 * Sizes use rem so they scale with the surrounding text. Border-radius is
 * intentionally generous on `lg` / `xl` (premium feel per design system).
 *
 * Avoids any side-stripe border decoration (Impeccable ban) and uses
 * tone-tinted backgrounds via color-mix when only color is available.
 */
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/api/client';

interface Props {
  name: string;
  image_key?: string | null;
  emoji?: string | null;
  color?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'rounded' | 'circle';
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<Props['size']>, string> = {
  xs: 'size-5 text-[10px]',
  sm: 'size-7 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-base',
  xl: 'size-20 text-xl',
};

const SHAPE_CLASS: Record<NonNullable<Props['shape']>, string> = {
  rounded: 'rounded-lg',
  circle: 'rounded-full',
};

export function ProjectAvatar({
  name,
  image_key,
  emoji,
  color,
  size = 'md',
  shape = 'rounded',
  className,
}: Props) {
  const sizeCls = SIZE_CLASS[size];
  const shapeCls = SHAPE_CLASS[shape];
  if (image_key) {
    // loading=lazy + decoding=async keep off-screen avatars (e.g., a long
    // ProjectsList grid or a 20-node orbital) out of the critical path.
    // aria-hidden because alt="" plus the surrounding label/name already
    // carries the accessible identity; double-announce otherwise.
    return (
      <img
        src={`${API_BASE}/v1/files/${image_key}`}
        alt=""
        loading="lazy"
        decoding="async"
        aria-hidden
        className={cn(sizeCls, shapeCls, 'shrink-0 object-cover', className)}
      />
    );
  }
  if (emoji) {
    return (
      <span
        className={cn(
          sizeCls,
          shapeCls,
          'inline-grid shrink-0 place-items-center bg-card leading-none',
          className,
        )}
        aria-hidden
      >
        <span style={{ fontSize: '1.4em' }}>{emoji}</span>
      </span>
    );
  }
  const initial = (name || '?')[0]!.toUpperCase();
  const tone = color ?? 'oklch(0.68 0.19 265)';
  return (
    <span
      className={cn(
        sizeCls,
        shapeCls,
        'inline-grid shrink-0 place-items-center font-semibold uppercase tracking-tight',
        className,
      )}
      style={{
        background: `color-mix(in oklch, ${tone} 28%, transparent)`,
        color: tone,
      }}
      aria-hidden
    >
      {initial}
    </span>
  );
}
